import type { SelectOption } from "@kanda-libs/ks-component-ts";
import type {
  FinanceProvider,
  LenderConfig,
} from "@kanda-libs/ks-frontend-services";
import { capitaliseWords } from "common/helpers";
import { FINANCE_PROVIDERS } from "./constants";

export interface LenderConfigRow {
  provider: FinanceProvider | "";
  xid: string;
  isNew: boolean;
}

export interface EditLenderConfigFormValues {
  configs: LenderConfigRow[];
}

export const providerLabel = (provider: FinanceProvider | ""): string =>
  provider ? capitaliseWords(provider.replace(/_/g, " ")) : "";

export const toConfigRows = (
  lenderConfigs: LenderConfig[] | undefined,
): LenderConfigRow[] =>
  (lenderConfigs || []).map((config) => ({
    provider: config.provider,
    xid: config.xid,
    isNew: false,
  }));

// Providers still available to a new row: any provider not already selected by
// another row (a company cannot have two configs for the same lender).
export const availableProviders = (
  configs: LenderConfigRow[],
  currentIndex: number,
): SelectOption[] => {
  const used = new Set(
    configs
      .filter((_, index) => index !== currentIndex)
      .map((config) => config.provider)
      .filter(Boolean),
  );

  return [
    { name: "Select a lender...", value: "" },
    ...FINANCE_PROVIDERS.filter((provider) => !used.has(provider)).map(
      (provider) => ({
        name: providerLabel(provider),
        value: provider,
      }),
    ),
  ];
};

export interface ValidatedConfigs {
  valid: boolean;
  error?: string;
  lenderConfigs: LenderConfig[];
}

export const validateConfigs = (
  rows: LenderConfigRow[],
): ValidatedConfigs => {
  const lenderConfigs: LenderConfig[] = [];
  const seen = new Set<string>();

  for (const row of rows) {
    const provider = row.provider;
    const xid = (row.xid || "").trim();
    if (!provider) {
      return { valid: false, error: "Select a lender for each row", lenderConfigs: [] };
    }
    if (!xid) {
      return {
        valid: false,
        error: `Enter a user id for ${providerLabel(provider)}`,
        lenderConfigs: [],
      };
    }
    if (seen.has(provider)) {
      return {
        valid: false,
        error: `Duplicate lender ${providerLabel(provider)}`,
        lenderConfigs: [],
      };
    }
    seen.add(provider);
    lenderConfigs.push({ provider, xid });
  }

  return { valid: true, lenderConfigs };
};
